import { useEffect, useMemo, useState } from "react";
import { WhitelistResponse, WhitelistWalletScore } from "../../../common/types";
import { getWhitelistData } from "../../helpers/utils";

const COPY_PRESETS = [10, 25, 50, 100];
const SHIELD_FP_COST = 6;
const LOOTBOX_USD_AVERAGE_COST = 1.5;
const DICE_ENTRY_FP_COST = 0.2;
const STAR_COLLECTION_GAS_USD = 0.005;
const SCORE_WEIGHTS = {
  stakedUsdValue: 1,
  shieldUsdValue: 2,
  lootboxUsdValue: 2,
  diceGamesEntered: 0.5,
  longestPetAliveDays: 0.1,
  stars: 0.1,
};
type WhitelistSortKey =
  | "score"
  | "staked"
  | "age"
  | "shield"
  | "loot"
  | "stars"
  | "dice";

function formatWallet(wallet: string): string {
  return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatScore(score: number): string {
  return Math.round(score).toString();
}

function formatUsd(value: number): string {
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatUsdPrecise(value: number): string {
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatEth(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 ETH";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 4 })} ETH`;
}

function formatFp(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} FP`;
}

function formatFpAmount(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatWeight(weight: number): string {
  return Number.isInteger(weight) ? weight.toString() : weight.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function formatYearsAndDays(totalDays: number): string {
  const days = Math.max(0, Math.round(totalDays));
  const years = Math.floor(days / 365);
  const remainingDays = days % 365;

  if (years === 0) return `${remainingDays}d`;
  return `${years}y ${remainingDays}d`;
}

function HeaderLabel({
  icon,
  label,
  className = "",
}: {
  icon: string;
  label: string;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <img src={icon} alt="" className="h-4 w-4" />
      <span>{label}</span>
    </div>
  );
}

function SortHeader({
  icon,
  label,
  sortKey,
  onSort,
}: {
  icon: string;
  label: string;
  sortKey: WhitelistSortKey;
  onSort: (sortKey: WhitelistSortKey) => void;
}) {
  return (
    <button
      type="button"
      className="flex items-center justify-center gap-1 hover:underline"
      onClick={() => onSort(sortKey)}
      aria-label={`Sort by ${label} greatest to least`}
    >
      <img src={icon} alt="" className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

function getWalletSortValue(wallet: WhitelistWalletScore, sortKey: WhitelistSortKey): number {
  switch (sortKey) {
    case "score":
      return wallet.score;
    case "staked":
      return wallet.stakedFp;
    case "age":
      return wallet.longestPetAliveDays;
    case "shield":
      return wallet.shieldsPurchased;
    case "loot":
      return wallet.lootboxesOpened;
    case "stars":
      return wallet.stars;
    case "dice":
      return wallet.diceGamesEntered;
    default:
      return wallet.score;
  }
}

function MetricsCard({
  data,
  fpPrice,
  ethPrice,
  isDarkMode,
}: {
  data: WhitelistResponse;
  fpPrice: number;
  ethPrice: number;
  isDarkMode: boolean;
}) {
  const panelClass = isDarkMode
    ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
    : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]";
  const metricCardClass = isDarkMode
    ? "border-darkgreen bg-darkblack [box-shadow:inset_0-2px_#37b764]"
    : "border-brown [box-shadow:inset_0_-2px_#342e2e]";
  const metricBgClasses = ["bg-skyblue", "bg-pink", "bg-green", "bg-copper", "bg-gold", "bg-pink", "bg-skyblue"];
  const metricSpanClasses = [
    "col-span-3 md:col-span-3",
    "col-span-3 md:col-span-3",
    "col-span-6 md:col-span-2",
    "col-span-3 md:col-span-2",
    "col-span-3 md:col-span-2",
    "col-span-3 md:col-span-3",
    "col-span-3 md:col-span-3",
  ];
  const allPets = data.wallets.flatMap((wallet) => wallet.pets);
  const totalStakedFp = data.wallets.reduce((total, wallet) => total + wallet.stakedFp, 0);
  const totalShields = data.wallets.reduce((total, wallet) => total + wallet.shieldsPurchased, 0);
  const totalShieldFp = totalShields * SHIELD_FP_COST;
  const totalLootboxes = data.wallets.reduce((total, wallet) => total + wallet.lootboxesOpened, 0);
  const totalLootboxUsd = totalLootboxes * LOOTBOX_USD_AVERAGE_COST;
  const totalDiceEntries = data.wallets.reduce((total, wallet) => total + wallet.diceGamesEntered, 0);
  const totalDiceFp = totalDiceEntries * DICE_ENTRY_FP_COST;
  const totalStarCollections = data.wallets.reduce((total, wallet) => total + wallet.stars, 0);
  const averagePlayerDays =
    data.wallets.length === 0
      ? 0
      : data.wallets.reduce((total, wallet) => total + wallet.longestPetAliveDays, 0) /
        data.wallets.length;
  const longestPet = allPets.reduce<WhitelistWalletScore["pets"][number] | null>(
    (longest, pet) => (!longest || pet.aliveDays > longest.aliveDays ? pet : longest),
    null,
  );
  const stakedUsd = totalStakedFp * fpPrice;
  const shieldsUsd = totalShieldFp * fpPrice;
  const starsGasUsd = totalStarCollections * STAR_COLLECTION_GAS_USD;
  const toEth = (usd: number) => (ethPrice > 0 ? usd / ethPrice : 0);
  const metrics = [
    {
      label: "Current Players",
      value: data.walletCount.toLocaleString(),
      details: [`${data.alivePetCount.toLocaleString()} alive pets`],
    },
    {
      label: "Average Time Played",
      value: formatYearsAndDays(averagePlayerDays),
      details: [longestPet
        ? `${longestPet.name} ${formatYearsAndDays(longestPet.aliveDays)}`
        : "Longest: none"],
    },
    {
      label: "Total Staked",
      value: formatFp(totalStakedFp),
      details: [formatUsd(stakedUsd), formatEth(toEth(stakedUsd))],
    },
    {
      label: "Shields Purchased",
      value: formatFp(totalShieldFp),
      details: [`${formatUsd(shieldsUsd)} spent`, formatEth(toEth(shieldsUsd))],
    },
    {
      label: "Lootboxes Opened",
      value: totalLootboxes.toLocaleString(),
      details: [`${formatUsd(totalLootboxUsd)} spent`, formatEth(toEth(totalLootboxUsd))],
    },
    {
      label: "Dice Entries",
      value: totalDiceEntries.toLocaleString(),
      details: [
        `${formatFp(totalDiceFp)} spent`,
        formatUsd(totalDiceFp * fpPrice),
        formatEth(toEth(totalDiceFp * fpPrice)),
      ],
    },
    {
      label: "Stars Collected",
      value: totalStarCollections.toLocaleString(),
      details: [`~$0.005/tx`, formatUsdPrecise(starsGasUsd), formatEth(toEth(starsGasUsd))],
    },
  ];

  return (
    <div className={`mb-4 w-full max-w-5xl rounded-md border p-3 text-sm ${panelClass}`}>
      <div className="mb-3 text-center text-lg font-semibold">Player Metrics</div>
      <div className="grid grid-cols-6 gap-2">
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={`flex min-h-[5.75rem] flex-col rounded-lg border-2 p-2.5 ${metricCardClass} ${
              isDarkMode ? "" : metricBgClasses[index % metricBgClasses.length]
            } ${metricSpanClasses[index]}`}
          >
            <div className="text-[0.68rem] font-semibold uppercase leading-tight opacity-75">
              {metric.label}
            </div>
            <div className="mt-1 text-lg font-semibold leading-tight">{metric.value}</div>
            <div className="mt-0.5 flex flex-col gap-0 text-xs leading-snug text-darkgrey">
              {metric.details.map((detail) => (
                <span key={detail}>{detail}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function shuffleWallets(wallets: WhitelistWalletScore[]): WhitelistWalletScore[] {
  const shuffled = [...wallets];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function WalletRow({
  wallet,
  expanded,
  onToggle,
  isDarkMode,
  shieldDisplay,
  lootDisplay,
  fpPrice,
  onShieldToggle,
  onLootToggle,
  onCopyWallet,
}: {
  wallet: WhitelistWalletScore;
  expanded: boolean;
  onToggle: () => void;
  isDarkMode: boolean;
  shieldDisplay: "count" | "fp" | "usd";
  lootDisplay: "count" | "usd";
  fpPrice: number;
  onShieldToggle: () => void;
  onLootToggle: () => void;
  onCopyWallet: (wallet: string) => void;
}) {
  const borderClass = isDarkMode ? "border-darkgreen" : "border-brown";
  const panelClass = isDarkMode
    ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
    : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]";
  const metricButtonClass = "hidden text-center md:block";
  const getPetScore = (pet: WhitelistWalletScore["pets"][number]) =>
    Math.round(
      pet.stakedFp * fpPrice * SCORE_WEIGHTS.stakedUsdValue +
        pet.aliveDays * SCORE_WEIGHTS.longestPetAliveDays +
        pet.shieldsPurchased * SHIELD_FP_COST * fpPrice * SCORE_WEIGHTS.shieldUsdValue +
        pet.lootboxesOpened * LOOTBOX_USD_AVERAGE_COST * SCORE_WEIGHTS.lootboxUsdValue +
        pet.stars * SCORE_WEIGHTS.stars +
        pet.diceGamesEntered * SCORE_WEIGHTS.diceGamesEntered,
    );

  return (
    <div className={`w-full max-w-5xl rounded-lg border-[1.5px] ${panelClass}`}>
      <div
        className="grid w-full grid-cols-[3rem_minmax(8rem,1fr)_5rem] items-center gap-2 px-3 py-3 text-left md:grid-cols-[4.5rem_minmax(12rem,1fr)_repeat(7,5.6rem)] md:gap-3"
      >
        <div className="text-lg">#{wallet.rank}</div>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className="truncate text-left text-base font-semibold hover:underline"
              onClick={onToggle}
            >
              {formatWallet(wallet.wallet)}
            </button>
            <button
              type="button"
              className={`hidden rounded border p-1 md:block ${borderClass}`}
              onClick={() => onCopyWallet(wallet.wallet)}
              aria-label={`Copy ${wallet.wallet}`}
            >
              <img
                src={isDarkMode ? "/imgs/copyDark.svg" : "/imgs/copy.svg"}
                alt=""
                className="h-4 w-4"
              />
            </button>
          </div>
          <div className="text-xs opacity-80">{wallet.alivePetCount} alive pets</div>
        </div>
        <div className="text-right text-lg font-semibold md:text-center">
          {formatScore(wallet.score)}
        </div>
        <div className="hidden text-center md:block">{formatFp(wallet.stakedFp)}</div>
        <div className="hidden text-center md:block">{wallet.longestPetAliveDays}</div>
        <button type="button" className={metricButtonClass} onClick={onShieldToggle}>
          {shieldDisplay === "count"
            ? wallet.shieldsPurchased
            : shieldDisplay === "fp"
              ? formatFp(wallet.shieldsPurchased * SHIELD_FP_COST)
              : formatUsd(wallet.shieldsPurchased * SHIELD_FP_COST * fpPrice)}
        </button>
        <button type="button" className={metricButtonClass} onClick={onLootToggle}>
          {lootDisplay === "count"
            ? wallet.lootboxesOpened
            : formatUsd(wallet.lootboxesOpened * LOOTBOX_USD_AVERAGE_COST)}
        </button>
        <div className="hidden text-center md:block">{wallet.stars}</div>
        <div className="hidden text-center md:block">{wallet.diceGamesEntered}</div>
      </div>

      {expanded && (
        <div className={`border-t ${borderClass} px-3 pb-3 pt-2`}>
          <div className="mb-2 grid grid-cols-8 gap-2 text-xs opacity-80">
            <div>Pet</div>
            <div className="text-center">Score</div>
            <div className="text-center">Staked</div>
            <div className="text-center">Age</div>
            <div className="text-center">Shield</div>
            <div className="text-center">Loot</div>
            <div className="text-center">Stars</div>
            <div className="text-center">Dice</div>
          </div>
          <div className="space-y-1">
            {wallet.pets.map((pet) => (
              <div
                key={pet.petId}
                className={`grid grid-cols-8 gap-2 rounded-md border px-2 py-1 text-sm ${borderClass}`}
              >
                <div className="min-w-0 truncate">
                  {pet.name} #{pet.petId}
                </div>
                <div className="text-center">{getPetScore(pet)}</div>
                <div className="text-center">
                  {formatFpAmount(pet.stakedFp)}
                  <span className="hidden md:inline"> FP</span>
                </div>
                <div className="text-center">{pet.aliveDays}</div>
                <button type="button" className="text-center" onClick={onShieldToggle}>
                  {shieldDisplay === "count"
                    ? pet.shieldsPurchased
                    : shieldDisplay === "fp"
                      ? formatFp(pet.shieldsPurchased * SHIELD_FP_COST)
                      : formatUsd(pet.shieldsPurchased * SHIELD_FP_COST * fpPrice)}
                </button>
                <button type="button" className="text-center" onClick={onLootToggle}>
                  {lootDisplay === "count"
                    ? pet.lootboxesOpened
                    : formatUsd(pet.lootboxesOpened * LOOTBOX_USD_AVERAGE_COST)}
                </button>
                <div className="text-center">{pet.stars}</div>
                <div className="text-center">{pet.diceGamesEntered}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Whitelist({
  isDarkMode,
  fpPrice,
  ethPrice,
}: {
  isDarkMode: boolean;
  fpPrice: number;
  ethPrice: number;
}) {
  const [data, setData] = useState<WhitelistResponse | null>(null);
  const [error, setError] = useState("");
  const [expandedWallet, setExpandedWallet] = useState<string | null>(null);
  const [copyAmount, setCopyAmount] = useState(10);
  const [copyStatus, setCopyStatus] = useState("");
  const [sortKey, setSortKey] = useState<WhitelistSortKey>("score");
  const [shieldDisplay, setShieldDisplay] = useState<"count" | "fp" | "usd">("count");
  const [lootDisplay, setLootDisplay] = useState<"count" | "usd">("count");
  useEffect(() => {
    getWhitelistData()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load whitelist"));
  }, []);

  const panelClass = isDarkMode
    ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
    : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]";
  const iconPaths = {
    wallet: isDarkMode ? "/imgs/walletDark.svg" : "/imgs/wallet.svg",
    score: isDarkMode ? "/imgs/piggy-bankDark.svg" : "/imgs/piggy-bank.svg",
    staked: isDarkMode ? "/imgs/frogDark.svg" : "/imgs/frog.svg",
    age: isDarkMode ? "/imgs/hourglassDark.svg" : "/imgs/hourglass.svg",
    shield: isDarkMode ? "/imgs/shieldDark.svg" : "/imgs/shield.svg",
    loot: isDarkMode ? "/imgs/slotDark.svg" : "/imgs/slot.svg",
    stars: isDarkMode ? "/imgs/starDark.svg" : "/imgs/star.svg",
    dice: isDarkMode ? "/imgs/diceDark.svg" : "/imgs/dice.svg",
    rank: isDarkMode ? "/imgs/rankDark.svg" : "/imgs/rank.svg",
  };

  const buttonClass = `rounded-md border-[1.5px] px-2 py-1 text-sm ${
    isDarkMode
      ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764] hover:bg-darkblack"
      : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e] hover:bg-green"
  }`;
  const sortedWallets = useMemo(() => {
    if (!data) return [];

    return [...data.wallets].sort((a, b) => {
      const valueDiff = getWalletSortValue(b, sortKey) - getWalletSortValue(a, sortKey);
      return valueDiff || a.rank - b.rank;
    });
  }, [data, sortKey]);

  const copyWallets = async (mode: "top" | "random") => {
    if (!data) return;

    const amount = Math.max(1, Math.min(copyAmount, data.wallets.length));
    const wallets =
      mode === "top" ? data.wallets.slice(0, amount) : shuffleWallets(data.wallets).slice(0, amount);
    const text = wallets.map((wallet) => wallet.wallet).join(" ");

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(`Copied ${amount} ${mode === "top" ? "top" : "random"} addresses`);
    } catch (_error) {
      setCopyStatus("Copy failed");
    }
  };

  const copyWallet = async (wallet: string) => {
    try {
      await navigator.clipboard.writeText(wallet);
      setCopyStatus(`Copied ${formatWallet(wallet)}`);
    } catch (_error) {
      setCopyStatus("Copy failed");
    }
  };

  return (
    <div className="mx-auto mb-8 flex w-full flex-col items-center overflow-x-hidden px-3">
      <div className={`mb-4 w-full max-w-5xl rounded-md border p-3 text-sm ${panelClass}`}>
        <div className="mb-1 font-semibold">Whitelist Scoring</div>
        <div>
          Score system updates once per day and uses current staked FP per alive pet, shields at 6
          FP each ({formatWeight(SCORE_WEIGHTS.shieldUsdValue)}x), lootboxes at $1.50 each (
          {formatWeight(SCORE_WEIGHTS.lootboxUsdValue)}x), dice{" "}
          ({formatWeight(SCORE_WEIGHTS.diceGamesEntered)}x), age{" "}
          ({formatWeight(SCORE_WEIGHTS.longestPetAliveDays)}x), stars{" "}
          ({formatWeight(SCORE_WEIGHTS.stars)}x).
        </div>
        {data && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {COPY_PRESETS.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`${buttonClass} ${copyAmount === amount ? "brightness-95" : ""}`}
                onClick={() => setCopyAmount(amount)}
              >
                {amount}
              </button>
            ))}
            <input
              type="text"
              inputMode="numeric"
              value={copyAmount}
              onChange={(event) => {
                const nextAmount = Number(event.target.value.replace(/\D/g, ""));
                if (Number.isFinite(nextAmount)) {
                  setCopyAmount(Math.max(1, Math.min(nextAmount || 1, data.wallets.length)));
                }
              }}
              className={`h-8 w-20 rounded-md border-[1.5px] px-2 text-center text-sm ${
                isDarkMode
                  ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
                  : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"
              }`}
            />
            <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
              <button
                type="button"
                className={`${buttonClass} w-full`}
                onClick={() => copyWallets("top")}
              >
                Copy Top
              </button>
              <button
                type="button"
                className={`${buttonClass} w-full`}
                onClick={() => copyWallets("random")}
              >
                Copy Random
              </button>
            </div>
            {copyStatus && (
              <div className={`text-xs ${isDarkMode ? "text-white" : "text-brown"}`}>
                {copyStatus}
              </div>
            )}
          </div>
        )}
      </div>

      {data && <MetricsCard data={data} fpPrice={fpPrice} ethPrice={ethPrice} isDarkMode={isDarkMode} />}

      {data && (
        <div className={`mb-2 hidden w-full max-w-5xl grid-cols-[4.5rem_minmax(12rem,1fr)_repeat(7,5.6rem)] gap-3 px-3 text-sm font-semibold md:grid ${isDarkMode ? "text-white" : "text-brown"}`}>
          <HeaderLabel icon={iconPaths.rank} label="Rank" />
          <HeaderLabel icon={iconPaths.wallet} label="Wallet" />
          <SortHeader icon={iconPaths.score} label="Score" sortKey="score" onSort={setSortKey} />
          <SortHeader icon={iconPaths.staked} label="Staked" sortKey="staked" onSort={setSortKey} />
          <SortHeader icon={iconPaths.age} label="Age" sortKey="age" onSort={setSortKey} />
          <SortHeader icon={iconPaths.shield} label="Shield" sortKey="shield" onSort={setSortKey} />
          <SortHeader icon={iconPaths.loot} label="Loot" sortKey="loot" onSort={setSortKey} />
          <SortHeader icon={iconPaths.stars} label="Stars" sortKey="stars" onSort={setSortKey} />
          <SortHeader icon={iconPaths.dice} label="Dice" sortKey="dice" onSort={setSortKey} />
        </div>
      )}

      <div className="flex w-full flex-col items-center gap-2">
        {!data && !error && (
          <div className={`rounded-lg border-[1.5px] px-4 py-3 ${panelClass}`}>Loading whitelist...</div>
        )}
        {error && <div className={`rounded-lg border-[1.5px] px-4 py-3 ${panelClass}`}>{error}</div>}
        {sortedWallets.map((wallet) => (
          <WalletRow
            key={wallet.wallet}
            wallet={wallet}
            expanded={expandedWallet === wallet.wallet}
            onToggle={() =>
              setExpandedWallet((current) => (current === wallet.wallet ? null : wallet.wallet))
            }
            isDarkMode={isDarkMode}
            shieldDisplay={shieldDisplay}
            lootDisplay={lootDisplay}
            fpPrice={fpPrice}
            onShieldToggle={() =>
              setShieldDisplay((current) =>
                current === "count" ? "fp" : current === "fp" ? "usd" : "count",
              )
            }
            onLootToggle={() =>
              setLootDisplay((current) => (current === "count" ? "usd" : "count"))
            }
            onCopyWallet={copyWallet}
          />
        ))}
      </div>

      {data && (
        <div className={`mt-3 w-full max-w-5xl text-left text-xs ${isDarkMode ? "text-white" : "text-brown"}`}>
          Updated {formatDate(data.updatedAt)}
        </div>
      )}
    </div>
  );
}
